/**
 * Payment Service
 *
 * API client for recording and managing payments against invoices.
 * Wraps the PaymentService proto RPCs (payment.proto).
 */

import { get, post, del, buildQueryString, type ApiResponse } from './api-client'

// ============================================================================
// Types
// ============================================================================

export type PaymentMethod =
  | 'PAYMENT_METHOD_BANK_TRANSFER'
  | 'PAYMENT_METHOD_CHEQUE'
  | 'PAYMENT_METHOD_CASH'
  | 'PAYMENT_METHOD_CREDIT_CARD'

export type PaymentStatus =
  | 'PAYMENT_STATUS_ENUM_DRAFT'
  | 'PAYMENT_STATUS_ENUM_POSTED'
  | 'PAYMENT_STATUS_ENUM_VOIDED'

export interface PaymentAllocation {
  id: string
  lineNumber: number
  invoiceId: string
  invoiceNumber: string
  invoiceDate: string
  invoiceTotal: number
  allocatedAmount: number
  baseAllocatedAmount: number
  notes?: string
  createdAt?: string
  updatedAt?: string
}

export interface Payment {
  id: string
  orgId: string
  ledgerId: string
  direction?: PaymentDirection
  paymentNumber: string
  paymentDate: string
  paymentMethod: PaymentMethod
  referenceNumber?: string
  referenceDate?: string
  bankAccountId?: string
  bankName?: string
  accountNumber?: string
  partyId: string
  partyName: string
  partyEntityId?: string
  currency: string
  exchangeRate?: string
  paymentAmount: number
  allocatedAmount: number
  unallocatedAmount: number
  baseCurrency?: string
  basePaymentAmount?: number
  baseAllocatedAmount?: number
  baseUnallocatedAmount?: number
  notes?: string
  status: PaymentStatus
  journalEntryId?: string
  allocations: PaymentAllocation[]
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  updatedBy?: string
  postedAt?: string
  postedBy?: string
  voidedAt?: string
  voidedBy?: string
  voidReason?: string
  tdsRate?: string
  tdsAmount?: number
  tdsAccountId?: string
}

export interface PaymentAllocationInput {
  invoiceId: string
  allocatedAmount: number
  notes?: string
}

export type PaymentDirection = 'inward' | 'outward'

export interface RecordPaymentRequest {
  direction?: PaymentDirection
  partyId: string
  partyName?: string
  paymentDate: string
  paymentAmount: number
  paymentMethod: PaymentMethod
  referenceNumber?: string
  referenceDate?: string
  bankAccountId?: string
  currency?: string
  exchangeRate?: string
  allocations: PaymentAllocationInput[]
  notes?: string
  intermediaryFeeAmount?: number  // Intermediary bank fee in payment (source) currency
  separateIntermediaryFee?: boolean // Separate bank line for reconciliation
  destinationFeeAmount?: number   // Destination bank fee in base currency
  separateDestinationFee?: boolean // Separate bank line for reconciliation
  feeAccountId?: string           // GL expense account for fees (empty = default)
  advanceAppliedAmount?: number   // Advance set-off in smallest currency units
  tdsRate?: string                // TDS rate e.g. "10.00" for 10%
  tdsAmount?: number              // TDS amount in smallest currency unit
  tdsAccountId?: string           // GL account for TDS
}

export interface ListPaymentsFilter {
  partyId?: string
  status?: PaymentStatus
  fromDate?: string
  toDate?: string
  direction?: PaymentDirection
}

export interface ListPaymentsResponse {
  payments: Payment[]
  nextPageToken?: string
  totalCount?: number
  /**
   * Lower bound (or exact count, see remainingIsExact) of rows known to
   * exist after this page, from the backend's "peek 3 pages ahead" window
   * (see server/internal/repository/pagination.go).
   */
  remainingCount?: number
  /** True when remainingCount is the exact remaining total; false => "many". */
  remainingIsExact?: boolean
}

export interface InvoicePaymentsResponse {
  allocations: PaymentAllocation[]
  totalPaid: number
  outstandingBalance: number
}

// ============================================================================
// API Functions
// ============================================================================

/** Record a new payment (draft). */
export async function recordPayment(
  orgId: string,
  ledgerId: string,
  data: RecordPaymentRequest
): Promise<ApiResponse<Payment>> {
  return post<Payment>(`/organizations/${orgId}/ledgers/${ledgerId}/payments`, data)
}

/** Post a draft payment (finalize and create journal entry). */
export async function postPayment(
  orgId: string,
  paymentId: string
): Promise<ApiResponse<Payment>> {
  return post<Payment>(`/organizations/${orgId}/payments/${paymentId}/post`, {})
}

/** Void a posted payment. */
export async function voidPayment(
  orgId: string,
  paymentId: string,
  reason: string
): Promise<ApiResponse<Payment>> {
  return post<Payment>(`/organizations/${orgId}/payments/${paymentId}/void`, { reason })
}

/** Get payment by ID. */
export async function getPayment(
  orgId: string,
  paymentId: string
): Promise<ApiResponse<Payment>> {
  return get<Payment>(`/organizations/${orgId}/payments/${paymentId}`)
}

/** List payments for a ledger with optional filters. */
export async function listPayments(
  orgId: string,
  ledgerId: string,
  filter?: ListPaymentsFilter,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<ListPaymentsResponse>> {
  const query = buildQueryString({
    partyId: filter?.partyId,
    status: filter?.status,
    fromDate: filter?.fromDate,
    toDate: filter?.toDate,
    direction: filter?.direction,
    pageSize,
    pageToken,
  })
  return get<ListPaymentsResponse>(`/organizations/${orgId}/ledgers/${ledgerId}/payments${query}`)
}

/** Delete a draft payment. */
export async function deleteDraftPayment(
  orgId: string,
  paymentId: string
): Promise<ApiResponse<void>> {
  return del<void>(`/organizations/${orgId}/payments/${paymentId}`)
}

/** Write off the remaining small balance on an invoice. */
export async function writeOffInvoiceBalance(
  orgId: string,
  invoiceId: string,
  notes?: string,
  writeOffType?: 'rounding' | 'bad_debt'
): Promise<ApiResponse<Payment>> {
  return post<Payment>(`/organizations/${orgId}/invoices/${invoiceId}/write-off`, { notes, writeOffType })
}

/** Reverse a write-off (rounding or bad debt) on an invoice. */
export async function reverseWriteOff(
  orgId: string,
  invoiceId: string,
  reason?: string
): Promise<ApiResponse<{ ok: boolean }>> {
  return post<{ ok: boolean }>(`/organizations/${orgId}/invoices/${invoiceId}/reverse-write-off`, { reason })
}

/** Get payments allocated to a specific invoice. */
export async function getInvoicePayments(
  orgId: string,
  invoiceId: string
): Promise<ApiResponse<InvoicePaymentsResponse>> {
  return get<InvoicePaymentsResponse>(`/organizations/${orgId}/invoices/${invoiceId}/payments`)
}

// ============================================================================
// Utility Functions
// ============================================================================

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  PAYMENT_METHOD_BANK_TRANSFER: 'Bank Transfer',
  PAYMENT_METHOD_CHEQUE: 'Cheque',
  PAYMENT_METHOD_CASH: 'Cash',
  PAYMENT_METHOD_CREDIT_CARD: 'Credit Card',
}

export function getPaymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_METHOD_LABELS[method] ?? String(method)
}

const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  PAYMENT_STATUS_ENUM_DRAFT: 'Draft',
  PAYMENT_STATUS_ENUM_POSTED: 'Posted',
  PAYMENT_STATUS_ENUM_VOIDED: 'Voided',
}

export function getPaymentStatusLabel(status: PaymentStatus): string {
  return PAYMENT_STATUS_LABELS[status] ?? String(status)
}

export function getPaymentStatusBadgeVariant(
  status: PaymentStatus
): 'secondary' | 'success' | 'error' {
  if (status === 'PAYMENT_STATUS_ENUM_POSTED') {return 'success'}
  if (status === 'PAYMENT_STATUS_ENUM_VOIDED') {return 'error'}
  return 'secondary'
}

export const PAYMENT_METHOD_OPTIONS = [
  { value: 'PAYMENT_METHOD_BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'PAYMENT_METHOD_CHEQUE', label: 'Cheque' },
  { value: 'PAYMENT_METHOD_CASH', label: 'Cash' },
  { value: 'PAYMENT_METHOD_CREDIT_CARD', label: 'Credit Card' },
]
